import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { equals } from 'ramda'

import SettingsGear from 'app/common/components/Settings'
import type { SurveyFilters } from 'app/common/types/surveys'
import PlainButton, { OpenFilterButton } from 'app/common/ui-next/plain-button'
import PlainStatusFilter from 'app/common/ui-next/plain-status-filter'
import PlainInputSearch from 'app/common/ui-next/plain-input-search'
import PlainPaging from 'app/common/ui-next/plain-paging'
import { ReportSection } from 'app/common/templates-next/report-template'
import { useSurveys } from 'app/dataProviders/surveys/hooks'
import { surveysSettingsKey } from 'app/reducers/settings/surveys'

import { Filters, emptySurveyFilters } from '../filters'
import { SurveysTable } from './SurveysTable'
import { useSurveysPagination } from './useSurveysPagination'
import styles from './SurveysList.module.scss'

const getDefaultFilters = (): SurveyFilters => ({ ...emptySurveyFilters })

export const SurveysList = () => {
  const history = useHistory()
  const [archived, setArchived] = useState(false)
  const defaultFilters = useMemo(getDefaultFilters, [])
  const [filters, setFilters] = useState(defaultFilters)
  const [filtersDraft, setFiltersDraft] = useState(defaultFilters)
  const [filtersShown, setFiltersShown] = useState(false)

  const { currentPage, currentPageIndex, offset, perPage, onPage, resetPagination } =
    useSurveysPagination()

  const params = useMemo(
    () => ({
      ...filters,
      archived,
      from: filters.from,
      to: filters.to,
      limit: perPage,
      offset,
    }),
    [archived, filters, offset, perPage],
  )

  const {
    data: { items, total },
    loading,
    request,
  } = useSurveys({ params })

  useEffect(() => {
    resetPagination()
  }, [perPage, resetPagination])

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / perPage))
    if (!loading && currentPage > lastPage) resetPagination()
  }, [currentPage, loading, perPage, resetPagination, total])

  const changeStatus = (status: string) => {
    const nextArchived = status === 'archived'
    if (archived === nextArchived) return
    resetPagination()
    setArchived(nextArchived)
    setFilters((current) => ({ ...current, from: null, to: null }))
    setFiltersDraft((current) => ({ ...current, from: null, to: null }))
  }

  const applyFilters = () => {
    resetPagination()
    setFilters(filtersDraft)
  }

  const resetFilters = () => {
    setFiltersDraft(defaultFilters)
    setFilters(defaultFilters)
    resetPagination()
  }

  const setSearch = (q: string) => {
    setFilters((current) => ({ ...current, q }))
    setFiltersDraft((current) => ({ ...current, q }))
    resetPagination()
  }

  return (
    <div className={styles.page}>
      <ReportSection
        header={archived ? 'Архив опросов' : 'Текущие опросы'}
        headerBar={
          <PlainButton onClick={() => history.push('/surveys/new')}>{'Создать'}</PlainButton>
        }
        footerBar={
          <div className={styles.footerActions}>
            {total > 0 && (
              <div className={styles.paging}>
                <PlainPaging
                  total={total}
                  perPage={perPage}
                  currentPageIndex={currentPageIndex}
                  currentPageSize={items.length}
                  onPage={onPage}
                />
              </div>
            )}
            <SettingsGear dataKey={surveysSettingsKey} />
          </div>
        }
        skipPadding
      >
        <div className={styles.toolbar}>
          <OpenFilterButton
            opened={filtersShown}
            onClick={() => setFiltersShown((current) => !current)}
          />
          <PlainInputSearch
            className={styles.search}
            value={filters.q}
            placeholder="Поиск"
            clearSearch={() => setSearch('')}
            onValue={setSearch}
          />
          <div className={styles.status}>
            <PlainStatusFilter
              title="Показать"
              currentStatus={archived ? 'archived' : 'current'}
              status2TranslationMap={
                new Map([
                  ['current', 'Текущие опросы'],
                  ['archived', 'Архив'],
                ])
              }
              status2ColorMap={
                new Map([
                  ['current', '#4caf50'],
                  ['archived', '#ef6c00'],
                ])
              }
              onClickItem={changeStatus}
            />
          </div>
        </div>
        {filtersShown && (
          <>
            <Filters value={filtersDraft} onChange={setFiltersDraft} grayBackground />
            <div className={styles.filterActions}>
              <PlainButton
                outline
                disabled={equals(filtersDraft, defaultFilters)}
                onClick={resetFilters}
              >
                {'Сбросить все фильтры'}
              </PlainButton>
              <PlainButton disabled={equals(filtersDraft, filters)} onClick={applyFilters}>
                {'Применить'}
              </PlainButton>
            </div>
          </>
        )}

        <SurveysTable
          items={items}
          loading={loading}
          perPage={perPage}
          archived={archived}
          onSuccess={() => {
            void request()
          }}
        />
      </ReportSection>
    </div>
  )
}
